import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { moderateContent } from '../../util/api';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = (actionPayload, options = {}) => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    // Keys of the free-text public user fields to moderate (e.g. the socials
    // field). Passed in from the component, which has the userFields config;
    // enum/array fields like interests are excluded there.
    const { moderatedPublicKeys = [] } = options;

    // Capture the previous values so we only moderate actual changes.
    const previousProfile = getState().user.currentUser?.attributes?.profile;
    const previousDisplayName = previousProfile?.displayName;
    const previousFirstName = previousProfile?.firstName;
    const previousLastName = previousProfile?.lastName;
    const previousBio = previousProfile?.bio;
    const previousPublicData = previousProfile?.publicData || {};

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));

        // Fire-and-forget moderation when the display name actually changed.
        const newDisplayName = actionPayload.displayName;
        if (newDisplayName && newDisplayName !== previousDisplayName) {
          moderateContent({ triggerType: 'username', content: newDisplayName });
        }

        // The display name is auto-derived from the first/last name, so moderate
        // the full name (first + last) when either part actually changed.
        const newFirstName = actionPayload.firstName;
        const newLastName = actionPayload.lastName;
        const firstNameChanged = newFirstName != null && newFirstName !== previousFirstName;
        const lastNameChanged = newLastName != null && newLastName !== previousLastName;
        if (firstNameChanged || lastNameChanged) {
          const fullName = [
            newFirstName != null ? newFirstName : previousFirstName,
            newLastName != null ? newLastName : previousLastName,
          ]
            .filter(Boolean)
            .join(' ')
            .trim();
          if (fullName) {
            moderateContent({ triggerType: 'name', content: fullName });
          }
        }

        // Likewise moderate the bio when it actually changed.
        const newBio = actionPayload.bio;
        if (newBio && newBio !== previousBio) {
          moderateContent({ triggerType: 'bio', content: newBio });
        }

        // And each free-text public field (e.g. the socials field) when it
        // actually changed — its own trigger, but reuses the bio prompt
        // server-side (same link-aware logic). Keyed by the real field key from
        // config, so the key's casing doesn't matter here.
        const newPublicData = actionPayload.publicData || {};
        moderatedPublicKeys.forEach(key => {
          const value = newPublicData[key];
          if (typeof value === 'string' && value.trim() && value !== previousPublicData[key]) {
            moderateContent({ triggerType: 'socials', content: value.trim() });
          }
        });
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

export const profileUpdateInProgressSelector = state => state.ProfileSettingsPage.updateInProgress;
